import { createFileRoute, notFound } from "@tanstack/react-router";
import { buildHead } from "@/lib/seo/head";
import { SITE_URL } from "@/lib/seo/routeMeta";
import { contentHeaders } from "@/lib/seo/contentCache";
import { blogPostQueryOptions } from "@/lib/blogQueries";
import { extractFaqs, extractHowToSteps } from "@/lib/aeoHelpers";
import { resolveStaticImageUrl } from "@/lib/imageFallback";
import BlogPost from "@/pages/BlogPost";

export const Route = createFileRoute("/blog/$slug")({
  // Server-side fetch: the article's title, description, canonical and
  // JSON-LD are in the initial HTML response, so a crawler that does not run
  // JavaScript sees a real article instead of an empty shell.
  loader: async ({ params, context }) => {
    const bundle = await context.queryClient.ensureQueryData(
      blogPostQueryOptions(params.slug),
    );
    if (!bundle.post) throw notFound();
    return bundle;
  },
  // Served fresh from the database on every cache miss, so a newly published
  // post is crawlable without a deploy. See src/lib/seo/contentCache.ts.
  headers: ({ loaderData }) => contentHeaders(Boolean(loaderData?.post)),
  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    const canonical = `${SITE_URL}/blog/${params.slug}`;

    if (!post) {
      return buildHead({
        title: "Article unavailable | Home Approach",
        description: "This article could not be loaded.",
        canonical,
        ogTitle: "Article unavailable | Home Approach",
        ogDescription: "This article could not be loaded.",
        ogType: "article",
        noIndex: true,
      });
    }

    const title = post.meta_title || `${post.title} | Home Approach`;
    const description =
      post.meta_description ||
      post.excerpt ||
      `Read ${post.title} on the Home Approach blog.`;

    const rawImage = post.og_image || post.cover_image_url;
    const image = rawImage
      ? resolveStaticImageUrl(
          rawImage.startsWith("http") ? rawImage : `${SITE_URL}${rawImage}`,
        )
      : undefined;

    const wordCount = post.content ? post.content.split(/\s+/).length : 0;
    const faqs = post.content ? extractFaqs(post.content) : [];
    // Mirrors BlogPost.tsx's own gating exactly (title match OR howto_meta
    // opt-in) so a post that opts in via howto_meta -- without a "How to"
    // title -- gets matching HowTo JSON-LD instead of just the visual step
    // cards.
    const howToSteps = /^how to /i.test(post.title) || post.howto_meta
      ? extractHowToSteps(post.content || "")
      : [];

    const graph: Record<string, unknown>[] = [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description,
        author: {
          "@type": "Person",
          name: post.author_name || "Home Approach Team",
        },
        publisher: {
          "@type": "Organization",
          name: "Home Approach",
          url: SITE_URL,
          logo: { "@type": "ImageObject", url: `${SITE_URL}/og-image.png` },
        },
        datePublished: post.published_at,
        dateModified: post.updated_at || post.published_at,
        mainEntityOfPage: canonical,
        image: image || `${SITE_URL}/og-image.png`,
        articleSection: post.category,
        keywords: post.tags?.join(", "),
        wordCount,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
          { "@type": "ListItem", position: 3, name: post.title, item: canonical },
        ],
      },
    ];

    if (faqs.length >= 3) {
      graph.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      });
    }

    if (howToSteps.length >= 3) {
      graph.push({
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: post.title,
        description,
        image: image || `${SITE_URL}/og-image.png`,
        step: howToSteps.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.name,
          text: s.text || s.name,
        })),
      });
    }

    return buildHead({
      title,
      description,
      canonical,
      ogTitle: post.og_title || post.title,
      ogDescription: post.og_description || description,
      ogType: "article",
      image,
      jsonLd: graph,
    });
  },
  component: BlogPost,
});
